/**
 * Automation flow ("régua") — pure model and validation.
 *
 * The flow is a directed graph the operator draws on a canvas. This module owns
 * the *shape* rules; it never sends anything and never touches a clock. The
 * dispatcher owns the *permission* rules (electoral window, suppression,
 * opt-in). Keeping the two apart matters: a régua that is structurally valid is
 * still not allowed to send, and a régua that is allowed to send is still
 * useless if it loops forever.
 *
 * Two invariants earn their keep here:
 *
 *  1. **No cycles.** A cycle in a marketing flow is not a stylistic problem, it
 *     is an infinite loop that mails the same person until the number is
 *     banned. It is rejected, not warned about.
 *  2. **Every action declares its class.** The electoral gate can only decide
 *     on a classified piece. An action without a class would reach the
 *     dispatcher and be blocked there — better to refuse to save it.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TipoEtapa = 'gatilho' | 'espera' | 'condicao' | 'acao' | 'divisao_ab' | 'fim'

export type CanalEnvio = 'email' | 'sms' | 'whatsapp'

/** Which branch an edge represents. `default` is the only outgoing edge of a
 *  linear step; `sim`/`nao` belong to a condition; `a`/`b` to an A/B split. */
export type Ramo = 'default' | 'sim' | 'nao' | 'a' | 'b'

export interface Aresta {
  para: string
  quando: Ramo
}

export interface Etapa {
  id: string
  tipo: TipoEtapa
  config: Record<string, unknown>
  posicao: { x: number; y: number }
  proximas: Aresta[]
}

export interface ProblemaRegua {
  /** Step the problem belongs to; null when it is about the flow as a whole. */
  etapaId: string | null
  codigo: CodigoProblema
  mensagem: string
}

export type CodigoProblema =
  | 'sem_gatilho'
  | 'gatilho_duplicado'
  | 'gatilho_sem_evento'
  | 'etapa_inalcancavel'
  | 'ciclo'
  | 'destino_inexistente'
  | 'ramo_invalido'
  | 'ramo_duplicado'
  | 'condicao_sem_ramos'
  | 'condicao_sem_regra'
  | 'divisao_sem_ramos'
  | 'divisao_percentual_invalido'
  | 'acao_sem_canal'
  | 'acao_sem_classe'
  | 'acao_sem_conteudo'
  | 'espera_invalida'
  | 'fim_com_saida'
  | 'saida_multipla'
  | 'sem_acao'

export interface ResultadoRegua {
  valida: boolean
  problemas: ProblemaRegua[]
  /** Steps in topological order — the order the engine will walk them. */
  ordem: string[]
  /** Channels the flow can actually emit on. Drives the operator's checklist. */
  canais: CanalEnvio[]
}

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

const TIPOS = new Set<TipoEtapa>(['gatilho', 'espera', 'condicao', 'acao', 'divisao_ab', 'fim'])
const CANAIS = new Set<CanalEnvio>(['email', 'sms', 'whatsapp'])

/** Classes the electoral gate understands. Duplicated deliberately instead of
 *  imported: this module must stay usable without pulling the gate in. */
const CLASSES = new Set(['transacional', 'obrigacional', 'institucional', 'promocional'])

/** Which branches each step type is allowed to emit. */
const RAMOS_PERMITIDOS: Record<TipoEtapa, Set<Ramo>> = {
  gatilho: new Set<Ramo>(['default']),
  espera: new Set<Ramo>(['default']),
  acao: new Set<Ramo>(['default']),
  condicao: new Set<Ramo>(['sim', 'nao']),
  divisao_ab: new Set<Ramo>(['a', 'b']),
  fim: new Set<Ramo>(),
}

/** Events a trigger can listen to. Kept closed on purpose — a typo in a trigger
 *  name is a flow that silently never fires. */
export const EVENTOS_GATILHO = [
  'lead_criado',
  'lead_importado',
  'formulario_enviado',
  'pagina_visitada',
  'estagio_alterado',
  'manual',
] as const

export type EventoGatilho = (typeof EVENTOS_GATILHO)[number]

/** Fields a condition may test. Anything else is a typo, not a feature. */
export const CAMPOS_CONDICAO = [
  'estagio',
  'origem',
  'uf',
  'municipio',
  'categoriaId',
  'optinEmail',
  'optinWhatsapp',
  'temEmail',
  'temTelefone',
  'abriuEmail',
  'clicouEmail',
  'visitouLanding',
  'preencheuLanding',
] as const

export type CampoCondicao = (typeof CAMPOS_CONDICAO)[number]

/**
 * Campos que perguntam sobre landing pages. Eles não comparam valor: a
 * pergunta é "aconteceu?" (operador `existe`) ou "não aconteceu?"
 * (`nao_existe`), opcionalmente restrita a uma lista de páginas em
 * `config.landingPageIds` — lista vazia ou ausente significa QUALQUER página.
 * A mesma convenção vale para o gatilho.
 */
export const CAMPOS_LANDING = new Set<CampoCondicao>(['visitouLanding', 'preencheuLanding'])

/** Eventos de gatilho que podem ser restritos a landing pages específicas. */
export const EVENTOS_COM_LANDING = new Set<EventoGatilho>([
  'lead_criado',
  'formulario_enviado',
  'pagina_visitada',
])

/** A lista de páginas de um gatilho/condição, normalizada. */
export function landingsDaConfig(config: Record<string, unknown>): string[] {
  return Array.isArray(config.landingPageIds)
    ? (config.landingPageIds as unknown[]).map(String).filter(Boolean)
    : []
}

export const OPERADORES_CONDICAO = ['igual', 'diferente', 'contem', 'existe', 'nao_existe'] as const

export type OperadorCondicao = (typeof OPERADORES_CONDICAO)[number]

// ---------------------------------------------------------------------------
// Per-step validation
// ---------------------------------------------------------------------------

function texto(v: unknown): string {
  return typeof v === 'string' ? v.trim() : ''
}

function validarGatilho(e: Etapa, p: ProblemaRegua[]): void {
  const evento = texto(e.config.evento)
  if (!EVENTOS_GATILHO.includes(evento as EventoGatilho)) {
    p.push({
      etapaId: e.id,
      codigo: 'gatilho_sem_evento',
      mensagem: 'Escolha o evento que dispara a régua.',
    })
  }
}

function validarEspera(e: Etapa, p: ProblemaRegua[]): void {
  const min = e.config.minutos
  // A wait of zero is not a wait — it is an operator who meant to type
  // something. Negative would walk the schedule backwards.
  if (typeof min !== 'number' || !Number.isFinite(min) || min <= 0 || min > 60 * 24 * 180) {
    p.push({
      etapaId: e.id,
      codigo: 'espera_invalida',
      mensagem: 'A espera deve ser de 1 minuto a 180 dias.',
    })
  }
}

function validarCondicao(e: Etapa, p: ProblemaRegua[]): void {
  const campo = texto(e.config.campo)
  const operador = texto(e.config.operador)
  const valorPreenchido = texto(e.config.valor) !== '' || typeof e.config.valor === 'boolean'
  const ehLanding = CAMPOS_LANDING.has(campo as CampoCondicao)
  // Campos de landing são perguntas de sim/não ("visitou?"); os demais
  // operadores não têm significado neles e virariam uma regra que nunca casa.
  const dispensaValor = operador === 'existe' || operador === 'nao_existe' || ehLanding

  if (
    !CAMPOS_CONDICAO.includes(campo as CampoCondicao) ||
    !OPERADORES_CONDICAO.includes(operador as OperadorCondicao) ||
    (!dispensaValor && !valorPreenchido) ||
    (ehLanding && operador !== 'existe' && operador !== 'nao_existe')
  ) {
    p.push({
      etapaId: e.id,
      codigo: 'condicao_sem_regra',
      mensagem: ehLanding
        ? 'Condição de landing page usa apenas “aconteceu” ou “não aconteceu”.'
        : 'Defina campo, operador e valor da condição.',
    })
  }

  const ramos = new Set(e.proximas.map((a) => a.quando))
  if (!ramos.has('sim') || !ramos.has('nao')) {
    p.push({
      etapaId: e.id,
      codigo: 'condicao_sem_ramos',
      mensagem: 'A condição precisa dos dois caminhos: sim e não.',
    })
  }
}

function validarDivisao(e: Etapa, p: ProblemaRegua[]): void {
  const pct = e.config.percentualA
  if (typeof pct !== 'number' || !Number.isFinite(pct) || pct < 1 || pct > 99) {
    p.push({
      etapaId: e.id,
      codigo: 'divisao_percentual_invalido',
      mensagem: 'O percentual do caminho A deve ficar entre 1% e 99%.',
    })
  }
  const ramos = new Set(e.proximas.map((a) => a.quando))
  if (!ramos.has('a') || !ramos.has('b')) {
    p.push({
      etapaId: e.id,
      codigo: 'divisao_sem_ramos',
      mensagem: 'O teste A/B precisa dos dois caminhos.',
    })
  }
}

function validarAcao(e: Etapa, p: ProblemaRegua[]): void {
  const canal = texto(e.config.canal)
  if (!CANAIS.has(canal as CanalEnvio)) {
    p.push({ etapaId: e.id, codigo: 'acao_sem_canal', mensagem: 'Escolha o canal do envio.' })
  }

  // Without a class the dispatcher's electoral gate has nothing to decide on,
  // and its fail-closed default would block the send at run time — long after
  // the operator left the screen. Refuse now, while they can fix it.
  if (!CLASSES.has(texto(e.config.classeComunicacao))) {
    p.push({
      etapaId: e.id,
      codigo: 'acao_sem_classe',
      mensagem:
        'Classifique a peça (transacional, obrigacional, institucional ou promocional) — sem isso o envio é bloqueado.',
    })
  }

  // Com template escolhido, assunto e corpo vêm DELE — exigi-los aqui
  // obrigaria o operador a digitar um texto que nunca será enviado.
  if (texto(e.config.templateId)) return

  const corpo = texto(e.config.corpo)
  const assunto = texto(e.config.assunto)
  if (!corpo || (canal === 'email' && !assunto)) {
    p.push({
      etapaId: e.id,
      codigo: 'acao_sem_conteudo',
      mensagem: canal === 'email' ? 'Preencha assunto e corpo — ou escolha um template.' : 'Preencha a mensagem — ou escolha um template.',
    })
  }
}

// ---------------------------------------------------------------------------
// Flow validation
// ---------------------------------------------------------------------------

/**
 * Validates the flow as a whole. Returns every problem found, not just the
 * first — an operator fixing one error at a time on a canvas gives up.
 */
export function validarRegua(etapas: readonly Etapa[]): ResultadoRegua {
  const problemas: ProblemaRegua[] = []
  const porId = new Map<string, Etapa>()

  for (const e of etapas) {
    if (TIPOS.has(e.tipo)) porId.set(e.id, e)
  }

  // ----------------------------------------------------------- edge sanity
  for (const e of porId.values()) {
    const permitidos = RAMOS_PERMITIDOS[e.tipo]
    const vistos = new Set<Ramo>()

    for (const a of e.proximas) {
      if (!porId.has(a.para)) {
        problemas.push({
          etapaId: e.id,
          codigo: 'destino_inexistente',
          mensagem: 'A ligação aponta para uma etapa que não existe.',
        })
        continue
      }
      if (!permitidos.has(a.quando)) {
        problemas.push({
          etapaId: e.id,
          codigo: e.tipo === 'fim' ? 'fim_com_saida' : 'ramo_invalido',
          mensagem:
            e.tipo === 'fim'
              ? 'A etapa final não pode ter saída.'
              : 'Esse tipo de etapa não aceita esse caminho.',
        })
        continue
      }
      if (vistos.has(a.quando)) {
        problemas.push({
          etapaId: e.id,
          codigo: a.quando === 'default' ? 'saida_multipla' : 'ramo_duplicado',
          mensagem:
            a.quando === 'default'
              ? 'A etapa só pode ter uma saída — use uma condição para ramificar.'
              : 'Há dois caminhos iguais saindo da mesma etapa.',
        })
        continue
      }
      vistos.add(a.quando)
    }
  }

  // ------------------------------------------------------------- per step
  for (const e of porId.values()) {
    if (e.tipo === 'gatilho') validarGatilho(e, problemas)
    else if (e.tipo === 'espera') validarEspera(e, problemas)
    else if (e.tipo === 'condicao') validarCondicao(e, problemas)
    else if (e.tipo === 'divisao_ab') validarDivisao(e, problemas)
    else if (e.tipo === 'acao') validarAcao(e, problemas)
  }

  // -------------------------------------------------------------- trigger
  const gatilhos = [...porId.values()].filter((e) => e.tipo === 'gatilho')
  if (gatilhos.length === 0) {
    problemas.push({
      etapaId: null,
      codigo: 'sem_gatilho',
      mensagem: 'A régua precisa de um gatilho.',
    })
  } else if (gatilhos.length > 1) {
    for (const g of gatilhos.slice(1)) {
      problemas.push({
        etapaId: g.id,
        codigo: 'gatilho_duplicado',
        mensagem: 'Só pode haver um gatilho por régua.',
      })
    }
  }

  const raiz = gatilhos[0]

  // ---------------------------------------------------- reachability + DAG
  // A single DFS from the trigger answers both questions: which steps are
  // reachable, and whether any back edge exists.
  const ordem: string[] = []
  const cor = new Map<string, 0 | 1 | 2>() // 0 unvisited, 1 on stack, 2 done
  const emCiclo = new Set<string>()

  function visitar(id: string): void {
    cor.set(id, 1)
    for (const a of porId.get(id)?.proximas ?? []) {
      const destino = porId.get(a.para)
      if (!destino) continue
      const c = cor.get(a.para) ?? 0
      if (c === 1) emCiclo.add(a.para)
      else if (c === 0) visitar(a.para)
    }
    cor.set(id, 2)
    ordem.push(id)
  }

  if (raiz) visitar(raiz.id)
  ordem.reverse()

  for (const id of emCiclo) {
    problemas.push({
      etapaId: id,
      codigo: 'ciclo',
      mensagem: 'Há um ciclo na régua — o mesmo lead voltaria a receber os envios sem parar.',
    })
  }

  if (raiz) {
    for (const e of porId.values()) {
      if (!cor.has(e.id)) {
        problemas.push({
          etapaId: e.id,
          codigo: 'etapa_inalcancavel',
          mensagem: 'Etapa solta — nada leva até ela.',
        })
      }
    }
  }

  // ------------------------------------------------------------- channels
  const canais: CanalEnvio[] = []
  for (const id of ordem) {
    const e = porId.get(id)!
    if (e.tipo !== 'acao') continue
    const c = texto(e.config.canal) as CanalEnvio
    if (CANAIS.has(c) && !canais.includes(c)) canais.push(c)
  }

  if (raiz && !ordem.some((id) => porId.get(id)?.tipo === 'acao')) {
    problemas.push({
      etapaId: null,
      codigo: 'sem_acao',
      mensagem: 'A régua não envia nada — inclua ao menos uma ação.',
    })
  }

  return { valida: problemas.length === 0, problemas, ordem, canais }
}

// ---------------------------------------------------------------------------
// Traversal
// ---------------------------------------------------------------------------

/**
 * Which step comes next. Returns null at the end of a path.
 *
 * `ramo` is what the caller decided (a condition's outcome, an A/B draw). For
 * linear steps it is ignored and the `default` edge is used.
 */
export function proximaEtapa(etapa: Etapa, ramo: Ramo = 'default'): string | null {
  const alvo = etapa.tipo === 'condicao' || etapa.tipo === 'divisao_ab' ? ramo : 'default'
  return etapa.proximas.find((a) => a.quando === alvo)?.para ?? null
}

/** Human label for a step — used by the canvas and by the audit trail. */
export function rotuloEtapa(etapa: Etapa): string {
  // "(2 páginas)" no rótulo é o que deixa visível, direto no canvas, que o
  // passo está restrito — um filtro invisível é um filtro que ninguém lembra.
  const paginas = landingsDaConfig(etapa.config)
  const sufixoLanding =
    paginas.length === 0 ? '' : paginas.length === 1 ? ' (1 página)' : ` (${paginas.length} páginas)`

  switch (etapa.tipo) {
    case 'gatilho': {
      const evento = texto(etapa.config.evento)
      const comLanding = EVENTOS_COM_LANDING.has(evento as EventoGatilho) ? sufixoLanding : ''
      return `Quando: ${evento || 'não definido'}${comLanding}`
    }
    case 'espera': {
      const m = Number(etapa.config.minutos) || 0
      if (m % 1440 === 0 && m >= 1440) return `Esperar ${m / 1440} dia(s)`
      if (m % 60 === 0 && m >= 60) return `Esperar ${m / 60} hora(s)`
      return `Esperar ${m} min`
    }
    case 'condicao': {
      const campo = texto(etapa.config.campo)
      if (CAMPOS_LANDING.has(campo as CampoCondicao)) {
        const verbo = campo === 'visitouLanding' ? 'visitou a landing' : 'preencheu a landing'
        const nega = texto(etapa.config.operador) === 'nao_existe' ? 'não ' : ''
        return `Se ${nega}${verbo}${sufixoLanding || ' (qualquer)'}`
      }
      return `Se ${campo || '…'} ${texto(etapa.config.operador) || '…'} ${texto(etapa.config.valor)}`
    }
    case 'divisao_ab':
      return `Teste A/B ${Number(etapa.config.percentualA) || 50}/${100 - (Number(etapa.config.percentualA) || 50)}`
    case 'acao':
      return `Enviar ${texto(etapa.config.canal) || '…'}`
    case 'fim':
      return 'Fim'
  }
}

/** A blank step of the given type, positioned where the operator dropped it. */
export function etapaPadrao(id: string, tipo: TipoEtapa, x: number, y: number): Etapa {
  const base = { id, tipo, posicao: { x, y }, proximas: [] as Aresta[] }
  switch (tipo) {
    case 'gatilho':
      return { ...base, config: { evento: 'lead_criado' } }
    case 'espera':
      return { ...base, config: { minutos: 1440 } }
    case 'condicao':
      return { ...base, config: { campo: 'temEmail', operador: 'existe', valor: '' } }
    case 'divisao_ab':
      return { ...base, config: { percentualA: 50 } }
    case 'acao':
      return {
        ...base,
        config: { canal: 'email', classeComunicacao: 'obrigacional', assunto: '', corpo: '' },
      }
    case 'fim':
      return { ...base, config: {} }
  }
}
